export enum WebAppRoutes {
  INVENTORY_ITEMS = "inventory-items",
  INVENTORY_POKEMONS = "inventory-pokemons",
  SHOP = "shop",
  SEND_POKEMONS = "send-pokemons",
  SEND_POKECOINS = "send-pokecoins",
  SEND_POKESHARDS = "send-pokeshards",
  SEND_ITEMS = "send-items",
  CREATE_POKE_TEAM = "create-poke-team",
  LIST_POKE_TEAMS = "list-poke-teams",
  UPDATE_POKE_TEAM = "update-poke-team",
  DELETE_POKE_TEAM = "delete-poke-team",
  PLAYER_BALANCE = "player-balance",
  PLAYER_PROFILE = "player-profile",
  UPDATE_PLAYER_PROFILE = "update-player-profile",
  CREATE_DUEL = "create-duel",
  UPDATE_POKEMON_SKILLS = "update-pokemon-skills",
  UPDATE_POKEMON_NAME = "pokemon/update-name",
  CALCULATE_BREED_COST = "calculate-breed-cost",
  BREED = "breed",
  HATCH = "hatch",
  ENQUEUE_PLAYER = "enqueue-player",
  GET_PLAYER_QUEUE_STATUS = "get-player-queue-status",
  DEQUEUE_PLAYER = "dequeue-player",
  QUIT_DUEL = "quit-duel",
  RELEASE_POKEMON = "release-pokemon",
  LEADERBOARD = "leaderboard",
  GET_EVOLUTION_OPTIONS = "get-evolution-options",
  EVOLVE_POKEMON = "evolve-pokemon",
  CLAIM_DAILY_REWARD = "player/claim-daily-reward",
  TEAM_SELECTION = "team-selection",
  JOIN_BATTLE = "join-battle",
  JOIN_GROUP_BATTLE = "join-group-battle",
  ONBOARDING = "onboarding",
  // Ideas routes
  GET_IDEA_BOARD = "ideas/board",
  CREATE_IDEA = "ideas/create",
  VOTE_IDEA = "ideas/vote",
  CLAIM_IDEA_REWARD = "ideas/claim-reward",
  MARK_SEEN = "mark-seen",
  // Training routes
  START_TRAINING_BATTLE = "training/start-battle",
  START_SANDBOX_BATTLE = "training/sandbox/start-battle",
  // Box routes
  CREATE_BOX = "box/create",
  UPDATE_BOX = "box/update",
  DELETE_BOX = "box/delete",
  GET_BOXES = "box/list",
  GET_BOX_DETAILS = "box/details",
  MOVE_POKEMONS = "box/move",
  // Collection routes
  GET_COLLECTIONS = "collection/list",
  GET_COLLECTION_DETAILS = "collection/details",
  SET_COLLECTION = "collection/set",
}

export enum RaidRoutes {
  CREATE_RAID = "raid/create",
  JOIN_RAID = "raid/join",
  GET_CURRENT_RAID = "raid/current",
}


// ============================================================================
// ONBOARDING
// ============================================================================

export interface OnboardingInput {
  action:
  | "create_player"
  | "update_country"
  | "pick_starter"
  | "complete"
  | "verify_username"
  | "get_status"
  | "request_alpha_access";
  username?: string | undefined;
  country?: string | undefined;
  starter_type?: string | undefined;
  starter_types?: string[] | undefined;
}

export interface OnboardingOutput {
  ok: boolean;
  message: string;
  available?: boolean;
  alpha_permission?: boolean;
  onboarding_completed?: boolean;
  player?: {
    id: string;
    username: string;
    country?: string | null;
  };
  starter?: Pick<
    Pokemon,
    "id" | "display_id" | "base_pokemon_name" | "level" | "active_skills"
  >;
  starters?: Array<
    Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "level" | "active_skills"
    >
  >;
}

// ============================================================================
// LEADERBOARD
// ============================================================================

export interface LeaderboardEntry {
  rank: number;
  username: string;
  score: number;
  user_id: string;
  country?: string;
}

export interface LeaderboardOutput {
  season: number;
  remaining_days: number;
  entries: LeaderboardEntry[];
  current_user_entry?: LeaderboardEntry;
}

// ============================================================================
// INVENTORY - ITEMS
// ============================================================================

import {
  PlayerItem,
  Pokemon,
  PokemonChildren,
  PlayerPokemonTeam,
} from "prisma-layer";
import { RaidDifficulty } from "./raids/types";
import { PlayerRole, RaidDataEntry, RaidStatus } from "./raid-dynamodb";

export interface InventoryItemsOutput {
  player_items: Pick<PlayerItem, "id" | "item_name" | "amount">[];
}

// ============================================================================
// INVENTORY - POKEMONS
// ============================================================================

export interface InventoryPokemonsOutput {
  player_pokemons: Array<
    Pick<
      Pokemon,
      | "id"
      | "display_id"
      | "name"
      | "base_pokemon_name"
      | "level"
      | "experience"
      | "state"
      | "talents"
      | "known_skills"
      | "active_skills"
      | "created_at"
      | "is_new"
      | "is_shiny"
      | "box_id"
      | "collection_id"
    > & {
      pokemon_children: Pick<PokemonChildren, "id">[];
      pokemon_parents: {
        parent: Pick<
          Pokemon,
          "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
        >;
      }[];
    }
  >;
}

// ============================================================================
// SHOP
// ============================================================================

export interface ShopInput {
  item_name: string;
  amount: number;
}

export interface ShopOutput {
  message: string;
  ok: boolean;
  player_items: Pick<PlayerItem, "id" | "item_name" | "amount">[];
}

// ============================================================================
// PLAYER PROFILE
// ============================================================================

export interface MatchHistory {
  duel_id: string;
  opponent_name: string;
  opponent_id: string;
  result: "win" | "loss" | "draw";
  timestamp: string;
  is_bot_match: boolean;
}

export interface RaidHistoryEntry {
  raid_id: string;
  raid_name: string;
  difficulty: string;
  outcome: "VICTORY" | "DEFEAT" | null;
  rewards: {
    coins: number;
    xp: number;
    boss_drops: string[];
  } | null;
  timestamp: string;
}

export interface PlayerProfileOutput {
  poke_coins: number;
  poke_shards: number;
  last_daily_reward_claim?: string | null;
  country?: string | null;
  username: string;
  alpha_permission: boolean;
  onboarding_completed: boolean;

  // Stats
  stats: {
    elo: number;
    wins: number;
    losses: number;
    duels: number;
    rank: number;
    rewarded_duels_today?: number;
  };

  // Main Team
  main_team: Array<
    Pick<
      Pokemon,
      | "id"
      | "display_id"
      | "base_pokemon_name"
      | "level"
      | "experience"
      | "nature"
      | "talents"
      | "active_skills"
      | "is_shiny"
    >
  >;

  // History
  match_history: MatchHistory[];
  raid_history: RaidHistoryEntry[];

  // Meta
  is_own_profile: boolean;
}

export interface UpdatePlayerProfileInput {
  country?: string;
}

export interface UpdatePlayerProfileOutput {
  message: string;
  ok: boolean;
  data: PlayerProfileOutput | null;
}

// ============================================================================
// SEND - POKEMONS
// ============================================================================

export interface SendPokemonsInput {
  pokemon_ids: number[];
  receiver_name: string;
}

export interface SendPokemonsOutput {
  pokemons: Array<
    Pick<Pokemon, "id" | "display_id" | "base_pokemon_name" | "is_shiny">
  >;
  receiver_name: string;
}

// ============================================================================
// SEND - POKECOINS
// ============================================================================

export interface SendPokecoinsInput {
  amount: number;
  receiver_name: string;
}

export interface SendPokecoinsOutput {
  amount_sent: number;
  current_balance: number;
  receiver_name: string;
}

// ============================================================================
// SEND - POKESHARDS
// ============================================================================

export interface SendPokeshardsInput {
  amount: number;
  receiver_name: string;
}

export interface SendPokeshardsOutput {
  amount_sent: number;
  current_balance: number;
  receiver_name: string;
}

// ============================================================================
// SEND - ITEMS
// ============================================================================

export interface SendItemsInput {
  items: Array<{ item_name: string; amount: number }>;
  receiver_name: string;
}

export interface SendItemsOutput {
  items: Array<Pick<PlayerItem, "id" | "item_name" | "amount">>;
  receiver_name: string;
}

// ============================================================================
// POKETEAM - CREATE
// ============================================================================

export interface CreateTeamInput {
  name: string;
}

export interface CreateTeamOutput {
  message: string;
  ok: boolean;
  team: Pick<PlayerPokemonTeam, "id" | "name"> | null;
}

// ============================================================================
// POKETEAM - LIST
// ============================================================================

export interface ListTeamsOutput {
  message: string;
  ok: boolean;
  teams: Array<
    Pick<PlayerPokemonTeam, "id" | "name" | "order"> & {
      pokemons: Array<
        Pick<
          Pokemon,
          | "id"
          | "display_id"
          | "base_pokemon_name"
          | "level"
          | "active_skills"
          | "is_shiny"
        >
      >;
      isMainTeam: boolean;
    }
  >;
}

// ============================================================================
// POKETEAM - UPDATE
// ============================================================================

export interface UpdateTeamInput {
  team_id: number;
  name?: string;
  order?: Record<string, number>;
  pokemon_ids?: number[];
  set_main_team?: boolean;
}

export interface UpdateTeamOutput {
  message: string;
  ok: boolean;
  team:
  | (Pick<PlayerPokemonTeam, "id" | "name" | "order"> & {
    pokemons: Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
    >[];
  })
  | null;
}

// ============================================================================
// POKETEAM - DELETE
// ============================================================================

export interface DeleteTeamInput {
  team_id: number;
}

export interface DeleteTeamOutput {
  message: string;
  ok: boolean;
}

// ============================================================================
// PLAYER DATA
// ============================================================================

export interface PlayerBalanceOutput {
  poke_coins: number;
  poke_shards: number;
}

// ============================================================================
// DUEL - CREATE
// ============================================================================

export interface CreateDuelInput {
  opponent_player_id: string;
  duel_draft_type: string;
  is_bot_match: boolean;
}

export interface CreateDuelOutput {
  ok: boolean;
  message: string;
  duel_id?: string;
  websocket_url?: string;
}

// ============================================================================
// POKEMON - UPDATE SKILLS
// ============================================================================

export interface UpdatePokemonSkillsInput {
  pokemon_id: number;
  active_skills: string[];
}

export interface UpdatePokemonSkillsOutput {
  message: string;
  ok: boolean;
  pokemon: Pick<Pokemon, "id" | "display_id" | "active_skills"> | null;
}

// ============================================================================
// POKEMON - UPDATE NAME
// ============================================================================

export interface UpdatePokemonNameInput {
  pokemon_id: number;
  name: string | null;
}

export interface UpdatePokemonNameOutput {
  message: string;
  ok: boolean;
  pokemon: Pick<Pokemon, "id" | "display_id" | "name"> | null;
}

// ============================================================================
// BREEDING - CALCULATE COST
// ============================================================================

export interface CalculateBreedCostInput {
  parent1_id: number;
  parent2_id: number;
}

export interface CalculateBreedCostOutput {
  costs: Array<{ coin: number; shard: number } | null>;
  parent1_breed_count: number;
  parent2_breed_count: number;
  can_breed: boolean;
}

// ============================================================================
// BREEDING - BREED
// ============================================================================

export interface BreedInput {
  parent1_id: number;
  parent2_id: number;
  amount?: number;
}

export interface BreedOutput {
  eggs: Array<
    Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "talents" | "created_at"
    >
  >;
  coin_cost: number;
  shard_cost: number;
}

// ============================================================================
// BREEDING - HATCH
// ============================================================================

export interface HatchInput {
  egg_id: number;
}

export interface HatchOutput {
  pokemon: Pick<
    Pokemon,
    | "id"
    | "display_id"
    | "base_pokemon_name"
    | "level"
    | "talents"
    | "known_skills"
    | "active_skills"
    | "is_shiny"
  >;
}

// ============================================================================
// DUEL - QUEUE
// ============================================================================

export interface EnqueuePlayerInput {
  queue_id: string;
  team_id: number;
}

export interface EnqueuePlayerOutput {
  queue_entry?: {
    queue_id: string;
    status: string;
    joined_at: number;
  };
}

export interface GetPlayerQueueStatusOutput {
  status: "IDLE" | "WAITING" | "MATCHED" | "IN_DUEL";
  queue_entry?: {
    queue_id: string;
    joined_at: number;
    duel_id?: string;
  };
}

export interface DequeuePlayerOutput {
  message: string;
  ok: boolean;
}

// ============================================================================
// POKEMON - RELEASE
// ============================================================================

export interface ReleasePokemonInput {
  pokemon_id: number;
}

export interface ReleasePokemonOutput {
  message: string;
  ok: boolean;
  released_pokemon: Pick<
    Pokemon,
    "id" | "display_id" | "base_pokemon_name" | "is_shiny"
  > | null;
  returned_items?: Array<{ item_name: string; amount: number }>;
}

// ============================================================================
// POKEMON - MARK SEEN
// ============================================================================

export interface MarkSeenInput {
  pokemon_id: number;
}

export interface MarkSeenOutput {
  message: string;
  ok: boolean;
}

// ============================================================================
// DUEL - QUIT
// ============================================================================

export interface QuitDuelInput {
  duel_id: string;
}

export interface QuitDuelOutput {
  success: boolean;
  message: string;
}

// ============================================================================
// POKEMON - EVOLUTION
// ============================================================================

export interface EvolutionOptionInfo {
  target_species: string;
  trigger_type: string;
  required_item?: string | null;
  required_level?: number | null;
  is_player_choice?: boolean;
  choice_label?: string;
}

export interface GetEvolutionOptionsInput {
  pokemon_id: number;
}

export interface GetEvolutionOptionsOutput {
  pokemon_id: number;
  pokemon_name: string;
  evolutions: EvolutionOptionInfo[];
  can_evolve: boolean;
}

export interface EvolvePokemonInput {
  pokemon_id: number;
  target_species: string;
}

export interface EvolvePokemonOutput {
  message: string;
  ok: boolean;
  pokemon: Pick<
    Pokemon,
    | "id"
    | "display_id"
    | "base_pokemon_name"
    | "level"
    | "known_skills"
    | "active_skills"
  > | null;
  consumed_item?: string;
}

// ============================================================================
// RAID - CREATE
// ============================================================================

export interface CreateRaidInput {
  raid_name: string;
  difficulty: RaidDifficulty;
}

export interface CreateRaidOutput {
  ok: boolean;
  message: string;
  raid_id?: string;
  join_code?: string;
  websocket_url?: string;
}

// ============================================================================
// RAID - GET CURRENT
// ============================================================================

export interface GetCurrentRaidOutput {
  raid_id: string | null;
  raid_status: RaidStatus | null;
}

// ============================================================================
// RAID - JOIN
// ============================================================================

export interface JoinRaidInput {
  join_code: string;
}

export interface JoinRaidOutput {
  ok: boolean;
  message: string;
  raid_id: string;
  raid_status: RaidStatus;
  websocket_url: string;
  player_role: PlayerRole;
}

// ============================================================================
// TEAM - SELECTION (Mini App)
// ============================================================================

export interface TeamSelectionOutput {
  team_id: number;
  team_name: string;
  slots: Array<{
    position: number;
    pokemon:
    | (Pick<
      Pokemon,
      | "id"
      | "display_id"
      | "base_pokemon_name"
      | "name"
      | "level"
      | "active_skills"
      | "is_shiny"
    > & {
      types: string[];
    })
    | null;
    busy: boolean;
    busy_reason?: string;
  }>;
}

// ============================================================================
// BATTLE - JOIN (Mini App / Callback)
// ============================================================================

export interface JoinBattleInput {
  battle_id: string;
  chat_id: string;
  selected_pokemon_id?: string;
  battle_type: "single" | "group";
}

export interface JoinBattleOutput {
  ok: boolean;
  joined: boolean;
  already_joined?: boolean;
  pokemon_used?: Pick<
    Pokemon,
    "id" | "display_id" | "base_pokemon_name" | "name" | "level" | "is_shiny"
  >;
  error_code?: string;
}

// ============================================================================
// IDEAS & VOTING
// ============================================================================

export interface IdeaData {
  id: number;
  player_id: string;
  title: string;
  content: string;
  status:
  | "PENDING"
  | "ADMIN_APPROVED"
  | "APPROVED_TO_PRIORITY_QUEUE"
  | "PRIORITY_DONE"
  | "REJECTED";
  created_at: string;
  votes: number; // For ranking/display
  my_vote?: number; // 1-5 or rank position
}

export interface GetIdeaBoardOutput {
  mode: "VOTING" | "RANKING";
  ideas: IdeaData[];
  reward_available: boolean;
  reward_claimed: boolean;
  votes_today: number;
}

export interface CreateIdeaInput {
  title: string;
  content: string;
}

export interface CreateIdeaOutput {
  ok: boolean;
  message: string;
  idea?: IdeaData;
}

export interface VoteIdeaInput {
  votes: Array<{
    idea_id: number;
    voting_score?: number;
    priority_score?: number;
  }>;
}

export interface VoteIdeaOutput {
  ok: boolean;
  message: string;
  votes_today?: number;
  reward_available?: boolean;
}

export interface ClaimIdeaRewardOutput {
  ok: boolean;
  message: string;
  reward?: {
    coins: number;
  };
}

export interface ClaimDailyRewardOutput {
  ok: boolean;
  message: string;
  reward?: {
    shards: number;
  };
}

// ============================================================================
// BOX MANAGEMENT
// ============================================================================

export interface PokemonBoxData {
  id: string;
  name: string;
  color: string;
  icon: string;
  pokemon_count?: number;
}

export interface CreateBoxInput {
  name: string;
  color?: string;
  icon?: string;
}

export interface CreateBoxOutput {
  ok: boolean;
  message: string;
  box: PokemonBoxData | null;
}

export interface UpdateBoxInput {
  box_id: string;
  name?: string;
  color?: string;
  icon?: string;
}

export interface UpdateBoxOutput {
  ok: boolean;
  message: string;
  box: PokemonBoxData | null;
}

export interface DeleteBoxInput {
  box_id: string;
}

export interface DeleteBoxOutput {
  ok: boolean;
  message: string;
}

export interface GetBoxesOutput {
  boxes: PokemonBoxData[];
}

export interface GetBoxDetailsInput {
  box_id: string; // "default" for null
}

export interface GetBoxDetailsOutput {
  box: PokemonBoxData | null; // null if default box
  pokemons: Array<
    Pick<
      Pokemon,
      "id" | "display_id" | "base_pokemon_name" | "level" | "is_shiny"
    > & { gender?: string }
  >;
}

export interface MovePokemonsInput {
  pokemon_ids: number[];
  target_box_id: string | null;
}

export interface MovePokemonsOutput {
  ok: boolean;
  message: string;
}

// ============================================================================
// COLLECTION MANAGEMENT
// ============================================================================

export interface CollectionData {
  id: string;
  display_name: string;
  region?: string;
  total_slots: number;
  obtained_count: number;
}

export interface GetCollectionsOutput {
  collections: CollectionData[];
}

export interface GetCollectionDetailsInput {
  collection_id: string;
}

export interface GetCollectionDetailsOutput {
  collection: {
    id: string;
    display_name: string;
    region?: string;
    slots: string[]; // base_pokemon_names
  };
  obtained_pokemons: Array<{
    base_pokemon_name: string;
    pokemon_id: number;
    display_id: string;
    is_shiny: boolean;
  }>;
  available_species: string[]; // Species the user owns but are not in a collection
}

export interface SetCollectionInput {
  pokemon_id: number;
  collection_id: string | null;
}

export interface SetCollectionOutput {
  ok: boolean;
  message: string;
}
